import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PageUrls } from '@constants/page-urls.constants';
import { DEFAULT_LOGO_URL } from '@models/layout-state.model';
import { CartInfo } from '@models/session-details';
import { ShoppingCartItem } from '@models/shopping-cart.model';
import {
  NgbAccordionDirective,
  NgbAccordionModule,
  NgbDropdown,
  NgbDropdownModule
} from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { ThemeService } from '@services/theme/theme.service';
import { BaseBadgeComponent } from '@shared-components/badge/base-badge/base-badge.component';
import { Subscription } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';
import tinycolor from 'tinycolor2';

@Component({
  selector: 'ts-header-bar',
  templateUrl: './header-bar.component.html',
  imports: [
    CommonModule,
    TranslationPipe,
    DataLoaderDirective,
    NgbDropdownModule,
    BaseBadgeComponent,
    NgbAccordionModule
  ]
})
export class HeaderBarComponent implements OnDestroy {
  @Input() navigate_to_dashboard: boolean = false;
  @Input() logo_url: string = DEFAULT_LOGO_URL;
  @ViewChild('userProfileDropdown') user_profile_dropdown: NgbDropdown;
  @ViewChild('themeSwitcher') theme_switcher: NgbAccordionDirective;
  user_application_info: { url: string | undefined; name: string | undefined };
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private orchestrator: OrchestratorService,
    private router: Router,
    private session_svc: SessionService,
    public theme_svc: ThemeService,
    private shopping_svc: ShoppingService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isUserLoggedIn(): boolean {
    return this.session_svc.hasUser();
  }

  getUserInitials(): string {
    return this.session_svc.getUserInitials();
  }

  getProfileUserName(): string | undefined {
    return this.session_svc.getUserFullName();
  }

  getEmail(): string {
    return this.session_svc.getEmail();
  }

  getIsMasquerading(): boolean {
    return this.session_svc.getIsMasquerading();
  }

  getBadgeStyleAttributes(): string {
    const branding = this.session_svc.getTenantInfo()?.branding;

    if (!branding?.primary_color) return '';

    const bg_color = tinycolor(branding.primary_color);

    if (!bg_color.isValid()) return '';

    const text_color: string = tinycolor.isReadable(bg_color, '#fff', {
      level: 'AA',
      size: 'large'
    })
      ? '#fff'
      : '#313536';

    return `background-color: ${bg_color.toHexString()} !important; color: ${text_color} !important`;
  }

  logout(): void {
    this.user_profile_dropdown.close();
    this.subscriptions.add(
      this.orchestrator.invalidateContext().subscribe((res: boolean) => {
        if (res) this.router.navigate([PageUrls.LOGOUT]);
      })
    );
  }

  canNavigateToDashboard(): boolean {
    return this.navigate_to_dashboard && this.session_svc.hasUser();
  }

  returnToDashboard(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.navigate_to_dashboard) return;

    if (this.activity_manager_svc.state().isValidCart(CartInfo.cart_data))
      this.activity_manager_svc.cancelCurrentActivity();
    else this.activity_manager_svc.clearCartInBackground();
  }

  getCartQuantity(): number | undefined {
    if (this.activity_manager_svc.state().isValidCart(CartInfo.cart_data)) {
      return CartInfo.cart_data?.shopping_cart_items.reduce(
        (accumulator, currentItems: ShoppingCartItem) => accumulator + currentItems.quantity!,
        0
      );
    }

    return undefined;
  }

  isPendoEnabled(): boolean {
    return this.shopping_svc.isPendoEnabled();
  }

  logoLoadError(event: any): void {
    if (event.target.src !== DEFAULT_LOGO_URL) event.target.src = DEFAULT_LOGO_URL;
  }

  dropDownOpenChange(open: boolean): void {
    if (open) return;
    this.theme_switcher.collapseAll();
  }

  getUserApplicationName(): string {
    return this.session_svc.getUserProfile()?.application_name || '';
  }

  navigateToUserApplication(): void {
    this.user_profile_dropdown.close();

    const application_url: string | undefined = this.session_svc.getUserProfile()?.application_url;
    if (application_url) window.open(application_url, '_blank');
  }
}
